import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sanitizeString, toValidObjectIdString } from '../utils/security';
import { fetchServiceRecordDetail, updateServiceRecordStatus } from '../services/serviceRecordsService';
import { getStoredUser } from '../services/apiClient';
import { navigateWithFrom } from '../utils/navigationUtils';
import './ComplaintDetailModal.css';

const safeText = (value) => sanitizeString(value ?? '');

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    let date;
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '—';
    }
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '—';
  }
};

const getPriorityBadgeStyle = (value = '') => {
  const v = String(value).toLowerCase();
  if (v.includes('acil')) return { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' };
  if (v.includes('yüksek') || v.includes('yuksek')) return { bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' };
  if (v.includes('orta')) return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
  if (v.includes('düşük') || v.includes('dusuk')) return { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' };
  return { bg: '#e5e7eb', color: '#374151', border: '#d1d5db' };
};

const getStatusBadgeStyle = (value = '') => {
  const v = String(value).toLowerCase();
  if (v.includes('açık') || v.includes('acik')) return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
  if (v.includes('çözüldü') || v.includes('cozuldu')) return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
  if (v.includes('tamamlandı') || v.includes('tamamlandi')) return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
  return { bg: '#e5e7eb', color: '#374151', border: '#d1d5db' };
};

const getTypeBadgeStyle = (value = '') => {
  const v = String(value).toLowerCase();
  if (v.includes('şikayet') || v.includes('sikayet')) {
    return { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', icon: 'fa-exclamation-circle' };
  }
  return { bg: '#eef2ff', color: '#3730a3', border: '#c7d2fe', icon: 'fa-lightbulb' };
};

const Field = ({ label, value, children }) => (
  <div className="cd-field">
    <span className="cd-field-label">{label}</span>
    <div className="cd-field-value">{children ?? (value || '—')}</div>
  </div>
);

const Badge = ({ text, style }) => (
  <span
    className="cd-badge"
    style={{
      background: style.bg,
      color: style.color,
      borderColor: style.border,
    }}
  >
    {style.icon ? <i className={`fas ${style.icon}`} style={{ marginRight: 6 }}></i> : null}
    {text}
  </span>
);

const ComplaintDetailModal = ({ servisKaydiId, onClose, onResolved }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Çözüm akışı state'leri
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  const currentUser = getStoredUser();
  const userRole = String(currentUser?.rol || '').toLowerCase();
  const isAdmin = userRole === 'admin';

  const loadData = useCallback(async () => {
    if (!servisKaydiId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchServiceRecordDetail(servisKaydiId);
      const sr = res?.veri?.servisKaydi || res?.veri?.serviceRecord || res?.veri || null;
      setRecord(sr);
    } catch (err) {
      setError(err?.message || 'Kayıt yüklenirken hata oluştu');
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [servisKaydiId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleResolveSubmit = async () => {
    setResolveError('');
    const not = resolveNote.trim();
    if (!not || not.length < 5) {
      setResolveError('Çözüm notu en az 5 karakter olmalıdır.');
      return;
    }
    setResolving(true);
    try {
      const res = await updateServiceRecordStatus(servisKaydiId, 'Çözüldü', { cozumNotu: not });
      const sr = res?.veri?.servisKaydi || null;
      if (sr) {
        setRecord(sr);
      } else {
        await loadData();
      }
      setShowResolveForm(false);
      setResolveNote('');
      if (window.showNotification) {
        window.showNotification('success', 'Kayıt çözüldü olarak işaretlendi.');
      }
      if (typeof onResolved === 'function') {
        onResolved(servisKaydiId);
      }
    } catch (err) {
      const msg = err?.data?.mesaj || err?.message || 'Çözüm işlemi başarısız oldu';
      setResolveError(msg);
    } finally {
      setResolving(false);
    }
  };

  if (!servisKaydiId) return null;

  const musteri = record?.musteri || record?.musteriId || {};
  const musteriAdi = `${safeText(musteri.adi || musteri.name || musteri.ad || '')} ${safeText(musteri.soyadi || musteri.surname || musteri.soyad || '')}`.trim() || '—';
  const musteriTelefon = safeText(musteri.telefon || musteri.phone || '') || '—';

  const olusturan = record?.olusturanKullanici || record?.olusturanKullaniciId || {};
  const olusturanAdi = (() => {
    if (typeof olusturan === 'string') return safeText(olusturan);
    const ad = `${safeText(olusturan.ad || olusturan.name || '')} ${safeText(olusturan.soyad || olusturan.surname || '')}`.trim();
    return ad || safeText(olusturan.kullaniciAdi || olusturan.email || '') || '—';
  })();

  const kategori = safeText(record?.istekSikayetKategorisi || '');
  const tur = safeText(record?.istekSikayetTuru || '');
  const durum = safeText(record?.durum || '');
  const onem = safeText(record?.onemDerecesi || '');
  const numara = safeText(record?.numara || '');
  const aciklama = safeText(record?.istekSikayetNotu || '');
  const tarih = record?.olusturmaTarihi || record?.createdAt;

  const cozumNotu = safeText(record?.cozumNotu || '');
  const cozumTarihi = record?.cozumTarihi;
  const cozumleyen = record?.cozumleyenKullanici || null;
  const cozumleyenAdi = (() => {
    if (!cozumleyen) return '';
    const adSoyad = `${safeText(cozumleyen.ad || '')} ${safeText(cozumleyen.soyad || '')}`.trim();
    return adSoyad || safeText(cozumleyen.kullaniciAdi || '');
  })();
  const isCozuldu = durum === 'Çözüldü';
  const canResolve = isAdmin && !isCozuldu && record;

  const hedefIcerik = record?.hedefIcerik || (record?.hedefIcerikId && typeof record.hedefIcerikId === 'object' ? record.hedefIcerikId : null);
  const hedefCihaz = record?.hedefCihaz || (record?.hedefCihazId && typeof record.hedefCihazId === 'object' ? record.hedefCihazId : null);
  const hedefTeknikServis = record?.hedefTeknikServis || (record?.hedefTeknikServisId && typeof record.hedefTeknikServisId === 'object' ? record.hedefTeknikServisId : null);
  const hedefServisKaydi = record?.hedefServisKaydi || (record?.hedefServisKaydiId && typeof record.hedefServisKaydiId === 'object' ? record.hedefServisKaydiId : null);
  const hedefServisKaydiId = toValidObjectIdString(
    hedefServisKaydi?.id ?? hedefServisKaydi?._id ?? record?.hedefServisKaydiId
  );

  const handleOpenServisKaydi = (targetServisKaydiId) => {
    if (!targetServisKaydiId) return;
    navigateWithFrom(navigate, `/service-records/${targetServisKaydiId}/update`, location);
    onClose?.();
  };

  const renderHedef = () => {
    if (!kategori) return <span className="cd-muted">—</span>;
    if (kategori === 'Ürün') {
      const ad = safeText(hedefIcerik?.icerikAdi || '');
      const kod = safeText(hedefIcerik?.icerikKodu || '');
      const seri = safeText(hedefCihaz?.seriNumarasi || '');
      if (!ad && !kod && !seri) return <span className="cd-muted">Ürün seçilmemiş</span>;
      return (
        <div className="cd-target-grid">
          <Field label="Ürün Adı" value={ad || '—'} />
          <Field label="Ürün Kodu" value={kod || '—'} />
          {seri ? <Field label="Cihaz Seri No" value={seri} /> : null}
        </div>
      );
    }
    if (kategori === 'Teknik Servis') {
      const ad = safeText(hedefTeknikServis?.ad || '');
      const kod = safeText(hedefTeknikServis?.kod || '');
      const tel = safeText(hedefTeknikServis?.telefon || '');
      const sk = hedefServisKaydi || null;
      if (!ad && !kod && !tel && !sk) return <span className="cd-muted">Teknik servis seçilmemiş</span>;
      return (
        <div>
          <div className="cd-target-grid">
            <Field label="Teknik Servis" value={ad || '—'} />
            <Field label="Kod" value={kod || '—'} />
            <Field label="Telefon" value={tel || '—'} />
          </div>
          {sk ? (
            <div className="cd-subsection">
              <div className="cd-subsection-title">İlgili Servis Kaydı</div>
              <div className="cd-target-grid">
                <Field label="Numara">
                  {hedefServisKaydiId ? (
                    <button
                      type="button"
                      className="cd-servis-kaydi-link"
                      onClick={() => handleOpenServisKaydi(hedefServisKaydiId)}
                      title="Servis kaydına git"
                    >
                      <i className="fas fa-clipboard-list" aria-hidden="true"></i>
                      <span>{safeText(sk.numara || '') || '—'}</span>
                      <i className="fas fa-arrow-right cd-servis-kaydi-link-arrow" aria-hidden="true"></i>
                    </button>
                  ) : (
                    safeText(sk.numara || '') || '—'
                  )}
                </Field>
                <Field label="İşlem" value={safeText(sk.islemTipi || '') || '—'} />
                <Field label="Durum" value={safeText(sk.durum || '') || '—'} />
              </div>
            </div>
          ) : null}
        </div>
      );
    }
    return <span className="cd-muted">Bu kategori için hedef seçimi yok.</span>;
  };

  return (
    <div className="modal-overlay complaint-detail-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-comment-dots" style={{ marginRight: 8, color: '#6366f1' }}></i>
            İstek / Şikayet Detayı
            {numara ? <span className="cd-numara">{numara}</span> : null}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="cd-loading">
              <div className="loading-spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="cd-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          ) : !record ? (
            <div className="cd-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>Kayıt bulunamadı.</span>
            </div>
          ) : (
            <div className="cd-content">
              <section className="cd-section">
                <div className="cd-section-title">
                  <i className="fas fa-user"></i> Müşteri
                </div>
                <div className="cd-target-grid">
                  <Field label="Ad Soyad" value={musteriAdi} />
                  <Field label="Telefon" value={musteriTelefon} />
                </div>
              </section>

              <section className="cd-section">
                <div className="cd-section-title">
                  <i className="fas fa-clipboard-list"></i> Kayıt
                </div>
                <div className="cd-target-grid">
                  <Field label="Numara" value={numara || '—'} />
                  <Field label="Kategori" value={kategori || '—'} />
                  <Field label="Tür">
                    {tur ? <Badge text={tur} style={getTypeBadgeStyle(tur)} /> : '—'}
                  </Field>
                  <Field label="Durum">
                    {durum ? <Badge text={durum} style={getStatusBadgeStyle(durum)} /> : '—'}
                  </Field>
                  <Field label="Önem Derecesi">
                    {onem ? <Badge text={onem} style={getPriorityBadgeStyle(onem)} /> : '—'}
                  </Field>
                  <Field label="Oluşturan" value={olusturanAdi} />
                  <Field label="Oluşturma Tarihi" value={formatDateTime(tarih)} />
                </div>
              </section>

              <section className="cd-section">
                <div className="cd-section-title">
                  <i className="fas fa-bullseye"></i> Hedef
                </div>
                {renderHedef()}
              </section>

              <section className="cd-section">
                <div className="cd-section-title">
                  <i className="fas fa-align-left"></i> Açıklama
                </div>
                <div className="cd-description">
                  {aciklama ? aciklama : <span className="cd-muted">Açıklama girilmemiş.</span>}
                </div>
              </section>

              {isCozuldu ? (
                <section className="cd-section cd-resolved">
                  <div className="cd-section-title cd-section-title-success">
                    <i className="fas fa-check-circle"></i> Çözüm
                  </div>
                  <div className="cd-target-grid" style={{ marginBottom: 12 }}>
                    <Field label="Çözüm Tarihi" value={formatDateTime(cozumTarihi)} />
                    <Field label="Çözen Kullanıcı" value={cozumleyenAdi || '—'} />
                  </div>
                  <div className="cd-description">
                    {cozumNotu ? cozumNotu : <span className="cd-muted">Çözüm notu girilmemiş.</span>}
                  </div>
                </section>
              ) : null}

              {canResolve && showResolveForm ? (
                <section className="cd-section cd-resolve-form">
                  <div className="cd-section-title cd-section-title-success">
                    <i className="fas fa-check-double"></i> Çözüldü Olarak İşaretle
                  </div>
                  <p className="cd-help-text">
                    Bu kaydı <strong>Çözüldü</strong> durumuna almak için kısa bir çözüm notu girin (en az 5 karakter).
                  </p>
                  <textarea
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value.substring(0, 2000))}
                    rows={5}
                    maxLength={2000}
                    placeholder="Yapılan işlem / verilen yanıt / çözüm açıklaması..."
                    className="cd-textarea"
                    disabled={resolving}
                  />
                  <div className="cd-textarea-meta">
                    <span>{resolveNote.length}/2000</span>
                    {resolveError ? (
                      <span className="cd-form-error">
                        <i className="fas fa-exclamation-triangle"></i> {resolveError}
                      </span>
                    ) : null}
                  </div>
                  <div className="cd-form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowResolveForm(false);
                        setResolveNote('');
                        setResolveError('');
                      }}
                      disabled={resolving}
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary cd-resolve-confirm"
                      onClick={handleResolveSubmit}
                      disabled={resolving || resolveNote.trim().length < 5}
                    >
                      {resolving ? (
                        <>
                          <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
                          İşleniyor...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check" style={{ marginRight: 6 }}></i>
                          Çözüldü Olarak Kaydet
                        </>
                      )}
                    </button>
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {canResolve && !showResolveForm ? (
            <button
              type="button"
              className="btn btn-primary cd-resolve-btn"
              onClick={() => {
                setShowResolveForm(true);
                setResolveError('');
              }}
            >
              <i className="fas fa-check-double" style={{ marginRight: 6 }}></i>
              Çözüldü Olarak İşaretle
            </button>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-times" style={{ marginRight: 6 }}></i>Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplaintDetailModal;
